import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
  IconCloud,
  IconHistory,
} from '@tabler/icons-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip } from '@/components/ui/tooltip';
import { api, UnauthorizedError } from '../api.js';
import { formatBytes, relativeTime } from '@/lib/utils';
import { Sparkline } from '../dam/Sparkline.js';

const STALE_CHECKPOINT_MS = 30 * 3600 * 1000;
const STALE_SNAPSHOT_MS = 15 * 60 * 1000;

type StatusTone = 'green' | 'red' | 'blue' | 'neutral';

function statusTone(status: string | null | undefined): StatusTone {
  if (status === 'success') return 'green';
  if (status === 'failed') return 'red';
  if (status === 'in-progress') return 'blue';
  return 'neutral';
}

export function AdminBackupsSection() {
  const summary = useQuery({
    queryKey: ['admin.backups.snapshot'],
    queryFn: () => api.adminBackupSnapshot(),
    refetchInterval: 30_000,
  });
  const history = useQuery({
    queryKey: ['admin.backups.history'],
    queryFn: () => api.adminBackupHistory(168),
    refetchInterval: 5 * 60_000,
  });
  const [showLog, setShowLog] = useState(false);
  const logTail = useQuery({
    queryKey: ['admin.backups.log-tail'],
    queryFn: () => api.adminBackupLogTail(),
    enabled: showLog,
    refetchInterval: showLog ? 10_000 : false,
  });

  if (summary.error instanceof UnauthorizedError) return null;
  if (summary.error) {
    const msg = summary.error.message;
    if (/admin only/i.test(msg)) return null;
    return (
      <Alert tone="destructive">
        <AlertDescription>{msg}</AlertDescription>
      </Alert>
    );
  }
  if (!summary.data) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-4">
          <Spinner size="xs" />
          <span className="text-xs text-muted-foreground">
            Loading backup state…
          </span>
        </CardContent>
      </Card>
    );
  }
  const snap = summary.data.snapshot;
  if (!snap) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-4">
          <Spinner size="xs" />
          <span className="text-sm text-muted-foreground">
            {summary.data.message ??
              'Snapshot pending — first run within 5 min of backend boot.'}
          </span>
        </CardContent>
      </Card>
    );
  }

  const snapshotAge = Date.now() - new Date(snap.takenAt).getTime();
  const checkpointAge = snap.latestCheckpointModifiedAt
    ? Date.now() - new Date(snap.latestCheckpointModifiedAt).getTime()
    : null;
  const snapshotStale = snapshotAge > STALE_SNAPSHOT_MS;
  const checkpointStale =
    checkpointAge !== null && checkpointAge > STALE_CHECKPOINT_MS;

  const tone = statusTone(snap.lastRunStatus);

  return (
    <div className="flex flex-col gap-4">
      {snapshotStale && (
        <Alert tone="warning">
          <IconAlertTriangle />
          <AlertDescription>
            The most recent snapshot is {relativeTime(snap.takenAt)} old —
            the backend snapshotter loop runs every 5 min. Check backend
            logs if this persists.
          </AlertDescription>
        </Alert>
      )}
      {checkpointStale && (
        <Alert tone="warning">
          <IconAlertTriangle />
          <AlertDescription>
            The newest checkpoint in R2 is{' '}
            {relativeTime(snap.latestCheckpointModifiedAt)} old. Daily cron
            should have run within 30h — investigate the host launchd
            agent.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-start gap-4">
        <Card className="min-w-[280px]">
          <CardContent className="flex flex-col gap-1.5 py-4">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <IconCloud size={13} />
              R2 mirror
            </span>
            <span className="text-xl font-bold tabular-nums">
              {formatBytes(snap.totalBytes)}
            </span>
            <span className="text-xs text-muted-foreground">
              {snap.totalObjects.toLocaleString()} objects
            </span>
            {history.data && history.data.points.length > 1 && (
              <TrendStrip
                values={history.data.points.map((p) => p.totalBytes)}
                label="7-day"
              />
            )}
            <span className="mt-1 text-xs text-muted-foreground">
              Snapshot {relativeTime(snap.takenAt)} (in {snap.snapshotMs} ms)
            </span>
          </CardContent>
        </Card>

        <Card className="min-w-[280px]">
          <CardContent className="flex flex-col gap-1.5 py-4">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Last mirror run
            </span>
            <div className="flex items-center gap-2">
              <Badge tone={tone}>{snap.lastRunStatus ?? 'unknown'}</Badge>
              <span className="text-xs text-muted-foreground">
                {snap.lastRunEndedAt
                  ? `ended ${relativeTime(snap.lastRunEndedAt)}`
                  : snap.lastRunStartedAt
                    ? `started ${relativeTime(snap.lastRunStartedAt)}`
                    : '—'}
              </span>
            </div>
            {snap.lastRunErrorTail && (
              <pre className="max-h-40 overflow-auto rounded-sm border border-neutral-800 bg-neutral-950 p-2 text-[10px] text-red-300">
                {snap.lastRunErrorTail}
              </pre>
            )}
            <button
              type="button"
              onClick={() => setShowLog((v) => !v)}
              className="mt-1 inline-flex items-center gap-1 text-xs text-theia-400 hover:underline"
            >
              {showLog ? (
                <IconChevronDown size={12} />
              ) : (
                <IconChevronRight size={12} />
              )}
              {showLog ? 'Hide log tail' : 'Show log tail (mirror.log)'}
            </button>
          </CardContent>
        </Card>

        <Card className="min-w-[280px]">
          <CardContent className="flex flex-col gap-1.5 py-4">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <IconHistory size={13} />
              Newest checkpoint
            </span>
            {snap.latestCheckpointName ? (
              <>
                <span className="font-mono text-sm font-medium">
                  {snap.latestCheckpointName}
                </span>
                <span className="text-xs text-muted-foreground">
                  {relativeTime(snap.latestCheckpointModifiedAt)}
                </span>
                <Tooltip
                  label={
                    snap.latestCheckpointModifiedAt
                      ? new Date(snap.latestCheckpointModifiedAt).toLocaleString()
                      : '—'
                  }
                >
                  <span className="cursor-help text-xs text-muted-foreground">
                    full timestamp ⓘ
                  </span>
                </Tooltip>
              </>
            ) : (
              <span className="text-xs text-muted-foreground">
                No checkpoints visible in R2.
              </span>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="py-4">
          <h3 className="mb-2 text-sm font-semibold">Per-prefix size</h3>
          <div className="rounded-md border border-neutral-800">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Prefix</TableHead>
                  <TableHead className="w-[140px]">Size</TableHead>
                  <TableHead className="w-[120px]">Objects</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code className="rounded-sm bg-neutral-800 px-1.5 py-0.5 text-[11px]">
                      checkpoints/
                    </code>
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {formatBytes(snap.checkpointsBytes)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {snap.checkpointsObjects.toLocaleString()}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code className="rounded-sm bg-neutral-800 px-1.5 py-0.5 text-[11px]">
                      archives/
                    </code>
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {formatBytes(snap.archivesBytes)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {snap.archivesObjects.toLocaleString()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {showLog && (
        <Card>
          <CardContent className="py-4">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Log tail
              </span>
              {logTail.data?.path && (
                <span className="text-xs text-muted-foreground">
                  {logTail.data.path}
                </span>
              )}
            </div>
            {logTail.isLoading ? (
              <Spinner size="xs" />
            ) : !logTail.data?.available ? (
              <span className="text-xs text-muted-foreground">
                No log file mounted.
              </span>
            ) : (
              <pre className="max-h-[360px] overflow-auto rounded-sm border border-neutral-800 bg-neutral-950 p-2 text-[11px]">
                {logTail.data.lines.join('\n')}
              </pre>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Sparkline + delta label. Shown beneath the per-card headline
// number to give "is this growing / stable / shrinking?" context
// at a glance.
function TrendStrip({
  values,
  label,
}: {
  values: number[];
  label: string;
}) {
  if (values.length < 2) return null;
  const first = values[0]!;
  const last = values[values.length - 1]!;
  const delta = last - first;
  const pct = first === 0 ? 0 : (delta / first) * 100;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const range = Math.max(...values) - Math.min(...values);
  const stable = mean > 0 && range / mean < 0.005;
  const deltaSign = delta > 0 ? '+' : delta < 0 ? '−' : '±';

  // Color the sparkline + delta label. Stable → neutral; growing → theia;
  // shrinking → red. Mapped to Tailwind theme tokens.
  const lineColor = stable
    ? 'var(--color-neutral-500)'
    : delta < 0
      ? 'var(--color-red-500)'
      : 'var(--color-theia-500)';
  let deltaClass: string;
  if (stable) deltaClass = 'text-muted-foreground';
  else if (delta < 0) deltaClass = 'text-red-400';
  else deltaClass = 'text-theia-400';

  const deltaLabel: ReactNode = stable
    ? 'stable'
    : `${deltaSign}${formatBytes(Math.abs(delta))} (${deltaSign}${Math.abs(pct).toFixed(1)}%)`;

  return (
    <div className="mt-1 flex flex-nowrap items-center gap-1.5">
      <Sparkline
        values={values}
        width={120}
        height={28}
        color={lineColor}
        ariaLabel={`${label} trend`}
      />
      <div className="flex flex-col">
        <span className={`text-xs font-medium ${deltaClass}`}>
          {deltaLabel}
        </span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}
